import React from 'react';
import { Home, Bookmark, Settings, CheckSquare } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

const Sidebar = () => {
  const location = useLocation();
  
  // Detect if running in side panel (based on URL or window width/height constraints, or a specific query param)
  // Or just rely on manifest.json side_panel default path being index.html?
  // Actually, for Side Panel, we might want a different view entirely or just hide other items?
  // The user said "Side bar, need quick switch bookmark list only".
  // Let's check if we are in a "narrow" view typical of side panel, or if we can detect context.
  // A simple way is to check window.innerWidth, but that's responsive design.
  // Let's assume if window width is small (< 600px), we are likely in side panel or popup.
  // But user specifically mentioned "Browser extension version, side bar...".
  // If we are in extension environment, we can check chrome.sidePanel context? Not directly in React easily without async.
  
  // However, the request is: "Browser extension version, sidebar, need quick switch bookmark list ONLY, no need to show others".
  // This implies when the app is opened in the Side Panel, the navigation should be minimal or different.
  // Maybe the Side Panel should just DEFAULT to bookmarks page and hide the sidebar?
  // Or the sidebar in Side Panel should only show "Bookmarks" icon? 
  // "Need quick switch bookmark list" might mean "Show the bookmark list directly" or "Only show bookmark nav item".
  // "No need to show others" implies hiding Home/Todos/Settings in the sidebar when in Side Panel mode.
  
  const isExtensionEnv = typeof window !== 'undefined' && window.location.protocol === 'chrome-extension:';
  const isSidePanel = isExtensionEnv && window.innerWidth < 500;

  const navItems = isSidePanel ? [
      { icon: Bookmark, label: '收藏', path: '/bookmarks' }
  ] : [
    { icon: Home, label: '首页', path: '/' },
    { icon: Bookmark, label: '收藏', path: '/bookmarks' },
    { icon: CheckSquare, label: '待办', path: '/todos' },
    { icon: Settings, label: '设置', path: '/settings' },
  ];

  // If in side panel, we might want to auto-redirect to bookmarks if on home?
  // We'll handle that in App.tsx or just let user click.
  // Actually, if we only show Bookmarks icon, user is stuck if they start at Home.
  // We should probably redirect to /bookmarks in App.tsx if isSidePanel.

  return (
    <div className="fixed left-4 top-1/2 -translate-y-1/2 z-50">
      <div className="flex flex-col gap-4 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border border-slate-200 dark:border-white/10 p-3 rounded-2xl shadow-2xl transition-colors duration-300">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link key={item.path} to={item.path}>
              <div className="relative group flex items-center justify-center">
                <motion.div
                  className={`p-3 rounded-xl transition-all duration-300 ${
                    isActive 
                      ? 'bg-blue-600 text-white shadow-[0_0_15px_rgba(37,99,235,0.5)]' 
                      : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/10'
                  }`}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <item.icon size={24} />
                </motion.div>
                
                {/* Tooltip */}
                <div className="absolute left-full ml-4 px-3 py-1.5 bg-white dark:bg-slate-800 text-slate-800 dark:text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none border border-slate-200 dark:border-white/10 shadow-xl">
                  {item.label}
                  <div className="absolute left-0 top-1/2 -translate-x-1 -translate-y-1/2 w-2 h-2 bg-white dark:bg-slate-800 rotate-45 border-l border-b border-slate-200 dark:border-white/10"></div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default Sidebar;
